import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient, { API_BASE_URL } from '../../api/apiClient';
import {
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import * as mammoth from 'mammoth';
import {
  VisibilityOutlined,
  Download,
  OpenInNew,
  Close,
  PictureAsPdf,
  ImageOutlined,
  VideoLibraryOutlined,
  AudiotrackOutlined,
  TextSnippetOutlined,
  DescriptionOutlined,
  InsertDriveFileOutlined,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import SideDrawerDialog from './SideDrawerDialog';
import { sanitizeHtml } from '../../utils/htmlSanitizer';
import { downloadFile } from '../../utils/downloadFile';

interface FileViewerProps {
  url: string;
  filename?: string;
  contentType?: string;
  trigger?: React.ReactNode;
  defaultFullscreen?: boolean;
  open?: boolean;
  onClose?: () => void;
  hideTrigger?: boolean;
}

type ViewerType = 'pdf' | 'image' | 'video' | 'audio' | 'text' | 'spreadsheet' | 'word' | 'presentation' | 'unsupported';

const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'avif', 'heic', 'heif', 'tif', 'tiff'];
const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'mkv'];
const audioExtensions = ['mp3', 'wav', 'm4a', 'aac', 'oga', 'opus'];
const spreadsheetExtensions = ['xls', 'xlsx', 'csv', 'ods'];
const textExtensions = ['txt', 'md', 'log', 'json', 'xml', 'html', 'htm', 'css', 'js', 'jsx', 'ts', 'tsx', 'yml', 'yaml'];
const wordExtensions = ['doc', 'docx', 'rtf', 'odt'];
const presentationExtensions = ['ppt', 'pptx', 'odp'];

const getExtension = (value?: string) => {
  if (!value) return '';
  const sanitized = value.split('?')[0].split('#')[0];
  const parts = sanitized.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

const resolveViewerType = (url: string, filename?: string, contentType?: string): ViewerType => {
  const extension = getExtension(filename) || getExtension(url);
  const mime = contentType?.toLowerCase() || '';

  if (extension === 'pdf' || mime.includes('application/pdf')) return 'pdf';
  if (imageExtensions.includes(extension) || mime.startsWith('image/')) return 'image';
  if (videoExtensions.includes(extension) || mime.startsWith('video/')) return 'video';
  if (audioExtensions.includes(extension) || mime.startsWith('audio/')) return 'audio';
  if (spreadsheetExtensions.includes(extension) || mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return 'spreadsheet';
  if (textExtensions.includes(extension) || mime.startsWith('text/') || mime.includes('application/json') || mime.includes('application/xml')) return 'text';
  if (wordExtensions.includes(extension)) return 'word';
  if (presentationExtensions.includes(extension)) return 'presentation';

  return 'unsupported';
};

const getColumnName = (index: number) => {
  let value = '';
  let current = index + 1;
  while (current > 0) {
    const remainder = (current - 1) % 26;
    value = String.fromCharCode(65 + remainder) + value;
    current = Math.floor((current - 1) / 26);
  }
  return value;
};

const decodeBufferText = (buffer: ArrayBuffer) => {
  try {
    return new TextDecoder('utf-8').decode(buffer);
  } catch {
    return '';
  }
};

const extractTextRunsFromXml = (xml: string, tagNames: string[]) => {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const values: string[] = [];
  tagNames.forEach(tagName => {
    Array.from(doc.getElementsByTagName(tagName)).forEach(node => {
      const text = node.textContent?.trim();
      if (text) values.push(text);
    });
  });
  return values;
};

const stripRtf = (rtf: string) => rtf
  .replace(/\\'[0-9a-fA-F]{2}/g, ' ')
  .replace(/\\par[d]?/g, '\n')
  .replace(/\\tab/g, '\t')
  .replace(/\\[a-zA-Z]+-?\d* ?/g, '')
  .replace(/[{}]/g, '')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const extractReadableBinaryText = (buffer: ArrayBuffer) => {
  const decoded = decodeBufferText(buffer);
  return decoded
    .split('')
    .map(char => {
      const code = char.charCodeAt(0);
      return code === 9 || code === 10 || code === 13 || (code >= 32 && code <= 126) ? char : ' ';
    })
    .join('')
    .split(/\s{2,}/)
    .map(part => part.trim())
    .filter(part => part.length > 2)
    .join('\n')
    .trim();
};

const formatTextAsHtml = (text: string) => sanitizeHtml(
  text
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => `<p>${line.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char] || char))}</p>`)
    .join('')
);

interface PresentationSlide {
  title: string;
  lines: string[];
}

const FileViewer: React.FC<FileViewerProps> = ({ url, filename = 'file', contentType, trigger, defaultFullscreen = true, open: openProp, onClose, hideTrigger = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(defaultFullscreen);
  const [authenticatedObjectUrl, setAuthenticatedObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string>('');
  const [textError, setTextError] = useState<string>('');
  const [spreadsheetSheets, setSpreadsheetSheets] = useState<string[]>([]);
  const [activeSheetName, setActiveSheetName] = useState<string>('');
  const [spreadsheetRowsBySheet, setSpreadsheetRowsBySheet] = useState<Record<string, unknown[][]>>({});
  const [spreadsheetError, setSpreadsheetError] = useState<string>('');
  const [wordHtml, setWordHtml] = useState<string>('');
  const [wordError, setWordError] = useState<string>('');
  const [presentationSlides, setPresentationSlides] = useState<PresentationSlide[]>([]);
  const [presentationError, setPresentationError] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const isControlled = openProp !== undefined;
  const dialogOpen = isControlled ? Boolean(openProp) : open;

  const resolvedUrl = useMemo(
    () => (url.startsWith('http://') || url.startsWith('https://') ? url : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`),
    [url]
  );

  const isApiFileProxy = useMemo(
    () => resolvedUrl.startsWith(`${API_BASE_URL}/api/files/`),
    [resolvedUrl]
  );

  const previewUrl = useMemo(
    () => (isApiFileProxy ? (resolvedUrl.includes('?') ? `${resolvedUrl}&download=false` : `${resolvedUrl}?download=false`) : resolvedUrl),
    [resolvedUrl, isApiFileProxy]
  );

  const downloadUrl = useMemo(
    () => (isApiFileProxy ? (resolvedUrl.includes('?') ? `${resolvedUrl}&download=true` : `${resolvedUrl}?download=true`) : resolvedUrl),
    [resolvedUrl, isApiFileProxy]
  );

  const viewerType = useMemo(() => resolveViewerType(previewUrl, filename, contentType), [previewUrl, filename, contentType]);
  const extension = useMemo(() => getExtension(filename) || getExtension(previewUrl), [filename, previewUrl]);
  const displayUrl = authenticatedObjectUrl || previewUrl;
  const isPdfReady = viewerType !== 'pdf' || !isApiFileProxy || Boolean(authenticatedObjectUrl);
  const activeSpreadsheetRows = spreadsheetRowsBySheet[activeSheetName] || [];
  const spreadsheetColumnCount = Math.max(1, ...activeSpreadsheetRows.map(row => row.length));

  const icon = useMemo(() => {
    if (viewerType === 'pdf') return <PictureAsPdf color="error" />;
    if (viewerType === 'image') return <ImageOutlined color="primary" />;
    if (viewerType === 'video') return <VideoLibraryOutlined color="primary" />;
    if (viewerType === 'audio') return <AudiotrackOutlined color="primary" />;
    if (viewerType === 'text') return <TextSnippetOutlined color="primary" />;
    if (viewerType === 'spreadsheet') return <DescriptionOutlined color="success" />;
    if (viewerType === 'word' || viewerType === 'presentation') return <DescriptionOutlined color="action" />;
    return <InsertDriveFileOutlined color="action" />;
  }, [viewerType]);

  const loadFileBuffer = useCallback(async () => {
    if (isApiFileProxy) {
      const response = await apiClient.get(previewUrl, { responseType: 'arraybuffer' });
      return response.data as ArrayBuffer;
    }

    const response = await fetch(displayUrl);
    if (!response.ok) throw new Error('Unable to load file preview.');
    return response.arrayBuffer();
  }, [displayUrl, isApiFileProxy, previewUrl]);

  const handleOpen = () => {
    setLoading(true);
    setFullscreen(defaultFullscreen);
    if (!isControlled) setOpen(true);
  };

  const handleClose = () => {
    if (!isControlled) setOpen(false);
    onClose?.();
    setFullscreen(false);
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError('');
    try {
      await downloadFile(downloadUrl, filename);
    } catch {
      setDownloadError('Unable to download this file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenInNew = () => {
    window.open(displayUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (!dialogOpen) return;
    setLoading(true);
    setFullscreen(defaultFullscreen);
  }, [dialogOpen, defaultFullscreen, previewUrl]);

  useEffect(() => {
    if (!dialogOpen || !isApiFileProxy || ['text', 'spreadsheet', 'word', 'presentation'].includes(viewerType)) return;

    let active = true;
    let objectUrl: string | null = null;
    setLoading(true);
    apiClient.get(previewUrl, { responseType: 'blob' })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setAuthenticatedObjectUrl(objectUrl);
      })
      .catch(() => setLoading(false));

    return () => {
      active = false;
      setAuthenticatedObjectUrl(null);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [dialogOpen, isApiFileProxy, previewUrl, viewerType]);

  useEffect(() => {
    if (!dialogOpen || viewerType !== 'text') return;

    let active = true;
    setLoading(true);
    setTextContent('');
    setTextError('');

    const loadText = async () => {
      try {
        if (isApiFileProxy) {
          const response = await apiClient.get(previewUrl, { responseType: 'text', transformResponse: [(data) => data] });
          if (active) setTextContent(String(response.data ?? ''));
          return;
        }

        const response = await fetch(displayUrl);
        if (!response.ok) throw new Error('Unable to load file preview.');
        const text = await response.text();
        if (active) setTextContent(text);
      } catch {
        if (active) setTextError('Text preview is unavailable for this file. You can still open or download it.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadText();

    return () => {
      active = false;
    };
  }, [dialogOpen, viewerType, isApiFileProxy, previewUrl, displayUrl]);

  useEffect(() => {
    if (!dialogOpen || viewerType !== 'spreadsheet') return;

    let active = true;
    setLoading(true);
    setSpreadsheetSheets([]);
    setActiveSheetName('');
    setSpreadsheetRowsBySheet({});
    setSpreadsheetError('');

    const loadSpreadsheet = async () => {
      try {
        const buffer = await loadFileBuffer();

        const workbook = XLSX.read(buffer, { type: 'array' });
        const rowsBySheet: Record<string, unknown[][]> = {};
        workbook.SheetNames.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          rowsBySheet[sheetName] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false,
          }) as unknown[][];
        });

        if (!active) return;
        setSpreadsheetSheets(workbook.SheetNames);
        setActiveSheetName(workbook.SheetNames[0] || '');
        setSpreadsheetRowsBySheet(rowsBySheet);
      } catch {
        if (active) setSpreadsheetError('Spreadsheet preview is unavailable for this file. You can still open or download it.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSpreadsheet();

    return () => {
      active = false;
    };
  }, [dialogOpen, viewerType, loadFileBuffer]);

  useEffect(() => {
    if (!dialogOpen || viewerType !== 'word') return;

    let active = true;
    setLoading(true);
    setWordHtml('');
    setWordError('');

    const loadWord = async () => {
      try {
        const buffer = await loadFileBuffer();
        let html = '';

        if (extension === 'docx') {
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          html = sanitizeHtml(result.value || '');
        } else if (extension === 'odt') {
          const zip = await JSZip.loadAsync(buffer);
          const contentXml = await zip.file('content.xml')?.async('string');
          const paragraphs = contentXml ? extractTextRunsFromXml(contentXml, ['text:h', 'text:p', 'text:span']) : [];
          html = formatTextAsHtml(paragraphs.join('\n'));
        } else if (extension === 'rtf') {
          html = formatTextAsHtml(stripRtf(decodeBufferText(buffer)));
        } else {
          html = formatTextAsHtml(extractReadableBinaryText(buffer));
        }

        if (!active) return;
        if (!html.trim()) {
          setWordError('No readable document content was found. You can still open or download it.');
        } else {
          setWordHtml(html);
        }
      } catch {
        if (active) setWordError('Document preview is unavailable for this file. You can still open or download it.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadWord();

    return () => {
      active = false;
    };
  }, [dialogOpen, viewerType, extension, loadFileBuffer]);

  useEffect(() => {
    if (!dialogOpen || viewerType !== 'presentation') return;

    let active = true;
    setLoading(true);
    setPresentationSlides([]);
    setPresentationError('');

    const loadPresentation = async () => {
      try {
        const buffer = await loadFileBuffer();
        let slides: PresentationSlide[] = [];

        if (extension === 'pptx') {
          const zip = await JSZip.loadAsync(buffer);
          const slideFiles = Object.keys(zip.files)
            .filter(name => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
            .sort((a, b) => {
              const aNumber = Number(a.match(/slide(\d+)\.xml/i)?.[1] || 0);
              const bNumber = Number(b.match(/slide(\d+)\.xml/i)?.[1] || 0);
              return aNumber - bNumber;
            });

          slides = await Promise.all(slideFiles.map(async (name, index) => {
            const xml = await zip.file(name)?.async('string');
            const lines = xml ? extractTextRunsFromXml(xml, ['a:t']) : [];
            return { title: `Slide ${index + 1}`, lines };
          }));
        } else if (extension === 'odp') {
          const zip = await JSZip.loadAsync(buffer);
          const contentXml = await zip.file('content.xml')?.async('string');
          if (contentXml) {
            const doc = new DOMParser().parseFromString(contentXml, 'application/xml');
            slides = Array.from(doc.getElementsByTagName('draw:page')).map((page, index) => {
              const lines = Array.from(page.getElementsByTagName('text:p'))
                .map(node => node.textContent?.trim() || '')
                .filter(Boolean);
              return { title: page.getAttribute('draw:name') || `Slide ${index + 1}`, lines };
            });
          }
        } else {
          const lines = extractReadableBinaryText(buffer).split('\n').filter(Boolean);
          slides = [{ title: 'Presentation', lines }];
        }

        if (!active) return;
        const visibleSlides = slides.filter(slide => slide.lines.length > 0);
        if (visibleSlides.length === 0) {
          setPresentationError('No readable slide content was found. You can still open or download it.');
        } else {
          setPresentationSlides(visibleSlides);
        }
      } catch {
        if (active) setPresentationError('Presentation preview is unavailable for this file. You can still open or download it.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPresentation();

    return () => {
      active = false;
    };
  }, [dialogOpen, viewerType, extension, loadFileBuffer]);

  const triggerNode = hideTrigger ? null : trigger
    ? React.isValidElement<{ onClick?: (event: React.MouseEvent) => void }>(trigger)
      ? React.cloneElement(trigger, {
          onClick: (event: React.MouseEvent) => {
            trigger.props.onClick?.(event);
            handleOpen();
          },
        })
      : (
        <Box onClick={handleOpen} sx={{ cursor: 'pointer', display: 'inline-flex' }}>
          {trigger}
        </Box>
      )
    : (
      <Tooltip title="View file">
        <IconButton size="small" onClick={handleOpen}>
          <VisibilityOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    );

  return (
    <>
      {triggerNode}

      <SideDrawerDialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth={fullscreen ? false : "lg"}
        fullWidth
        fullScreen={fullscreen}
        PaperProps={{ sx: { height: fullscreen ? '100vh' : '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {icon}
              <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ maxWidth: 420 }}>
                {filename}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
                <IconButton size="small" onClick={() => setFullscreen(prev => !prev)}>
                  {fullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton size="small" onClick={handleOpenInNew}><OpenInNew fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton aria-label={`Download ${filename}`} size="small" onClick={handleDownload} disabled={downloading}><Download fontSize="small" /></IconButton>
              </Tooltip>
              <IconButton size="small" onClick={handleClose}><Close fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          aria-busy={loading}
          sx={{ p: 0, flex: 1, overflow: 'hidden', position: 'relative' }}
        >
          {loading && viewerType !== 'unsupported' ? (
            <Box
              role="status"
              aria-live="polite"
              sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper', zIndex: 1 }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Loading document...</Typography>
            </Box>
          ) : null}

          {viewerType === 'pdf' && isPdfReady ? (
            <iframe
              src={`${displayUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              title={filename}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block' }}
              onLoad={() => setLoading(false)}
            />
          ) : null}

          {viewerType === 'image' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={displayUrl} alt={filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onLoad={() => setLoading(false)} onError={() => setLoading(false)} />
            </Box>
          ) : null}

          {viewerType === 'video' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <video controls style={{ width: '100%', height: '100%' }} onLoadedData={() => setLoading(false)} onError={() => setLoading(false)}>
                <source src={displayUrl} />
              </video>
            </Box>
          ) : null}

          {viewerType === 'audio' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Box sx={{ width: 'min(720px, 100%)', p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                  <AudiotrackOutlined color="primary" />
                  <Typography variant="subtitle2" fontWeight={700} noWrap>{filename}</Typography>
                </Box>
                <audio controls style={{ width: '100%' }} onCanPlay={() => setLoading(false)} onError={() => setLoading(false)}>
                  <source src={displayUrl} />
                </audio>
              </Box>
            </Box>
          ) : null}

          {viewerType === 'text' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'background.default', overflow: 'auto' }}>
              {textError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 2, px: 3, textAlign: 'center' }}>
                  <TextSnippetOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography color="text.secondary">{textError}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleOpenInNew}>Open File</Button>
                    <Button variant="outlined" startIcon={<Download />} onClick={handleDownload}>Download</Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    minHeight: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: 'text.primary',
                  }}
                >
                  {textContent}
                </Box>
              )}
            </Box>
          ) : null}

          {viewerType === 'spreadsheet' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {spreadsheetError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 2, px: 3, textAlign: 'center' }}>
                  <DescriptionOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography color="text.secondary">{spreadsheetError}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleOpenInNew}>Open File</Button>
                    <Button variant="outlined" startIcon={<Download />} onClick={handleDownload}>Download</Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', overflowX: 'auto', flex: '0 0 auto' }}>
                    {spreadsheetSheets.map(sheetName => (
                      <Button
                        key={sheetName}
                        size="small"
                        variant={sheetName === activeSheetName ? 'contained' : 'outlined'}
                        onClick={() => setActiveSheetName(sheetName)}
                        sx={{ minWidth: 90, flex: '0 0 auto' }}
                      >
                        {sheetName}
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%', bgcolor: 'background.paper' }}>
                      <Box component="thead">
                        <Box component="tr">
                          <Box
                            component="th"
                            sx={{
                              position: 'sticky',
                              top: 0,
                              left: 0,
                              zIndex: 3,
                              width: 48,
                              minWidth: 48,
                              bgcolor: 'grey.100',
                              borderRight: '1px solid',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                          {Array.from({ length: spreadsheetColumnCount }).map((_, columnIndex) => (
                            <Box
                              key={columnIndex}
                              component="th"
                              sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                minWidth: 132,
                                maxWidth: 280,
                                px: 1,
                                py: 0.75,
                                bgcolor: 'grey.100',
                                borderRight: '1px solid',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                fontSize: 12,
                                fontWeight: 800,
                                color: 'text.secondary',
                                textAlign: 'center',
                              }}
                            >
                              {getColumnName(columnIndex)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {activeSpreadsheetRows.length === 0 ? (
                          <Box component="tr">
                            <Box component="td" colSpan={spreadsheetColumnCount + 1} sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
                              This sheet is empty.
                            </Box>
                          </Box>
                        ) : activeSpreadsheetRows.map((row, rowIndex) => (
                          <Box component="tr" key={rowIndex}>
                            <Box
                              component="th"
                              sx={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                width: 48,
                                minWidth: 48,
                                px: 0.75,
                                py: 0.7,
                                bgcolor: 'grey.100',
                                borderRight: '1px solid',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                fontSize: 12,
                                fontWeight: 800,
                                color: 'text.secondary',
                                textAlign: 'right',
                              }}
                            >
                              {rowIndex + 1}
                            </Box>
                            {Array.from({ length: spreadsheetColumnCount }).map((_, columnIndex) => (
                              <Box
                                key={columnIndex}
                                component="td"
                                sx={{
                                  minWidth: 132,
                                  maxWidth: 280,
                                  px: 1,
                                  py: 0.7,
                                  borderRight: '1px solid',
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  fontSize: 12.5,
                                  lineHeight: 1.45,
                                  verticalAlign: 'top',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {String(row[columnIndex] ?? '')}
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          ) : null}

          {viewerType === 'word' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: '#f1f5f9', overflow: 'auto', p: { xs: 1.5, md: 3 } }}>
              {wordError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 2, px: 3, textAlign: 'center' }}>
                  <DescriptionOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography color="text.secondary">{wordError}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleOpenInNew}>Open File</Button>
                    <Button variant="outlined" startIcon={<Download />} onClick={handleDownload}>Download</Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 'min(920px, 100%)',
                    minHeight: '100%',
                    mx: 'auto',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: { xs: 2, md: 4 },
                    boxShadow: '0 14px 36px rgba(15,23,42,0.08)',
                    fontSize: 15,
                    lineHeight: 1.75,
                    color: 'text.primary',
                    '& p': { my: 1 },
                    '& h1, & h2, & h3': { mt: 2, mb: 1, lineHeight: 1.25 },
                    '& table': { borderCollapse: 'collapse', width: '100%', my: 2 },
                    '& td, & th': { border: '1px solid', borderColor: 'divider', p: 1 },
                    '& img': { maxWidth: '100%' },
                  }}
                  dangerouslySetInnerHTML={{ __html: wordHtml }}
                />
              )}
            </Box>
          ) : null}

          {viewerType === 'presentation' ? (
            <Box sx={{ width: '100%', height: '100%', bgcolor: '#eef2f7', overflow: 'auto', p: { xs: 1.5, md: 3 } }}>
              {presentationError ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 2, px: 3, textAlign: 'center' }}>
                  <DescriptionOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography color="text.secondary">{presentationError}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleOpenInNew}>Open File</Button>
                    <Button variant="outlined" startIcon={<Download />} onClick={handleDownload}>Download</Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 2, width: 'min(960px, 100%)', mx: 'auto' }}>
                  {presentationSlides.map((slide, index) => (
                    <Box
                      key={`${slide.title}-${index}`}
                      sx={{
                        aspectRatio: '16 / 9',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxShadow: '0 14px 36px rgba(15,23,42,0.08)',
                        p: { xs: 2, md: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ mb: 1 }}>{slide.title}</Typography>
                      <Stack gap={0.75}>
                        {slide.lines.slice(0, 12).map((line, lineIndex) => (
                          <Typography key={`${line}-${lineIndex}`} variant={lineIndex === 0 ? 'h6' : 'body2'} fontWeight={lineIndex === 0 ? 800 : 500}>
                            {line}
                          </Typography>
                        ))}
                        {slide.lines.length > 12 ? <Typography variant="caption" color="text.secondary">+{slide.lines.length - 12} more lines</Typography> : null}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ) : null}

          {viewerType === 'unsupported' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <InsertDriveFileOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography color="text.secondary">Preview not available for this file type.</Typography>
              <Button variant="contained" startIcon={<Download />} onClick={handleDownload}>Download File</Button>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ py: 1 }}>
          {downloadError ? <Typography color="error" variant="caption" sx={{ mr: 'auto' }}>{downloadError}</Typography> : null}
          <Button onClick={handleDownload} startIcon={downloading ? <CircularProgress size={16} /> : <Download />} size="small" disabled={downloading}>{downloading ? 'Downloading...' : 'Download'}</Button>
          <Button onClick={handleClose} size="small">Close</Button>
        </DialogActions>
      </SideDrawerDialog>
    </>
  );
};

export default FileViewer;
